/**
 * Shuffle array using Fisher-Yates algorithm
 * Supports optional seeding for deterministic shuffling
 */
export function shuffleArray<T>(array: T[], seed?: number): T[] {
  const result = [...array];
  
  for (let i = result.length - 1; i > 0; i--) {
    let j: number;
    
    if (seed !== undefined) {
      // Seeded random
      seed = (seed * 9301 + 49297) % 233280;
      j = Math.floor((seed / 233280) * (i + 1));
    } else {
      j = Math.floor(Math.random() * (i + 1));
    }
    
    [result[i], result[j]] = [result[j], result[i]];
  }
  
  return result;
}

/**
 * Deep clone a 2D array
 */
export function cloneGrid<T>(grid: T[][]): T[][] {
  return grid.map(row => [...row]);
}

/**
 * Generate UUID v4
 */
export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Format time in seconds to MM:SS
 */
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format time in seconds to human readable format
 */
export function formatTimeHuman(seconds: number): string {
  if (seconds < 60) {
    return `${seconds}s`;
  }
  
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  
  if (mins < 60) {
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
  }
  
  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  
  if (remainingMins > 0) {
    return `${hours}h ${remainingMins}m`;
  }
  
  return `${hours}h`;
}

/**
 * Create empty 9x9 Sudoku grid
 */
export function createEmptyGrid(): number[][] {
  return Array(9).fill(0).map(() => Array(9).fill(0));
}

/**
 * Check if two grids are equal
 */
export function gridsEqual(grid1: number[][], grid2: number[][]): boolean {
  if (grid1.length !== grid2.length) return false;
  
  for (let i = 0; i < grid1.length; i++) {
    if (grid1[i].length !== grid2[i].length) return false;
    
    for (let j = 0; j < grid1[i].length; j++) {
      if (grid1[i][j] !== grid2[i][j]) return false;
    }
  }
  
  return true;
}


